import React, { useEffect, useState } from "react";
import { Button } from "react-bootstrap";
import { useParams, useNavigate, Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft } from "@fortawesome/free-solid-svg-icons";
import VolumeCard from "../components/VolumeCard";
import { getPublishedArticles } from "../server/api";
import {
  formatJournalPath,
  parseJournalPath,
} from "../utils/formatJournalPath";
import { fetchJson } from "../utils/fetchJson";

const ArchivePage = () => {
  const [volumes, setVolumes] = useState([]);
  const [journals, setJournals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const { journalName } = useParams();
  const navigate = useNavigate();

  // 加载所有卷期数据
  useEffect(() => {
    const fetchVolumeData = async (url) => {
      const res = await fetchJson(url);
      if (!res.ok) throw new Error(`Failed to fetch data from ${url}`);
      const data = await res.json();
      const journal = data.journal;
      const volumes = data.volumes || [];
      return volumes.map((v) => ({ ...v, journal }));
    };

    Promise.all([
      fetchVolumeData(
        "/json/volumes/volumes/current-molecular-pharmacology-volume.json"
      ),
      fetchVolumeData(
        "/json/volumes/volumes/current-genomics-volume.json"
      ),
      fetchVolumeData(
        "/json/volumes/volumes/current-pharmaceutical-analysis-volume.json"
      ),
      fetchVolumeData(
        "/json/volumes/volumes/protein-peptide-letters.json"
      ),
    ])
      .then(([v1, v2, v3, v4]) => {
        setVolumes([...v1, ...v2, ...v3, ...v4]);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error loading volumes:", err);
        setError(err.message);
        setLoading(false);
      });
  }, []);

  // 当路径参数变化时（或刷新后），加载对应期刊文章数据
  useEffect(() => {
    if (!journalName) return; // 没有参数则不请求
    const fetchArticles = async () => {
      try {
        setLoading(true);
        const response = await getPublishedArticles({
          jcr: [parseJournalPath(journalName)],
          page: 1,
          limit: 8,
          order: "",
          years: [],
          opt: "issuelist",
        });
        setJournals(Array.isArray(response) ? response : []);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchArticles();
  }, [journalName]);

  const handleJournalClick = (journal) => {
    navigate(`/archive/${formatJournalPath(journal)}`);
  };

  if (loading) {
    return (
      <div className="container mt-4 text-center">
        <div className="spinner-border" role="status" />
        <p className="mt-2">Loading archives...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mt-4">
        <div className="alert alert-danger">
          <h4>Error Loading Archives</h4>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  // 如果是详情页
  if (journalName) {
    const decodedName = parseJournalPath(journalName);
    const journalVolumes = volumes.filter((v) => v.journal === decodedName);

    // 根据 journals 中的 volume 字段分组
    const groupedByVolume = journals.reduce((acc, item) => {
      const vol = item.volume || "Unknown";
      if (!acc[vol]) acc[vol] = [];
      acc[vol].push(item);
      return acc;
    }, {});

    // 渲染逻辑
    return (
      <div className="container mt-4">
        <nav aria-label="breadcrumb" className="mb-4">
          <ol className="breadcrumb m-0 p-0">
            <li className="breadcrumb-item">
              <Link to="/">Home</Link>
            </li>
            <li className="breadcrumb-item">
              <Link to="/archives">Archives</Link>
            </li>
            <li className="breadcrumb-item">
              <Link to={`/archive/${journalName}`}>
                {parseJournalPath(journalName)}
              </Link>
            </li>
          </ol>
        </nav>

        {journals.length === 0 && decodedName === "Protein & Peptide Letters" ? (
          <div className="alert alert-info p-4 mt-3 w-75 m-auto">
            <p className="mb-0" style={{ fontSize: "16px", lineHeight: "1.6", textAlign: "center" }}>
              The contents of Protein & Peptide Letters prior to Volume 34,
              published with Bentham Science Publishers, can be found here:
              <br/>
              <a
                href="https://www.benthamscience.com/journal/51/about-journal"
                target="_blank"
                rel="noopener noreferrer"
                className="ms-2 text-primary fw-bold"
              >
                https://www.benthamscience.com/journal/51/about-journal
              </a>
            </p>
          </div>
        ) : (
          // 正常渲染卷期列表
          Object.entries(groupedByVolume)
            // 按卷号（vol）从大到小排序
            .sort((a, b) => {
              const volA = parseFloat(a[0]) || 0;
              const volB = parseFloat(b[0]) || 0;
              return volB - volA; // 降序
            })
            .map(([vol, issues], index) => {
              const matchedVolume = journalVolumes.find(
                (v) => v.volume === vol
              ) || {
                cover_image: journalVolumes[0]?.cover_image || "",
              };

              return (
                <div key={index} className="col-12 mb-4">
                  <VolumeCard volume={matchedVolume} journals={issues} />
                </div>
              );
            })
        )}
      </div>
    );
  }

  // 否则显示所有期刊
  const uniqueJournals = Array.from(new Set(volumes.map((v) => v.journal))).map(
    (name) => {
      const sample = volumes.find((v) => v.journal === name);
      return {
        journal: name,
        cover_image: sample?.cover_image,
        title: sample?.title,
      };
    }
  );

  const comingSoonJournal = {
    journal: "More Journals Coming Soon",
    title: "More Journal",
    more: "···",
    isComingSoon: true,
  };

  const allJournals = [...uniqueJournals, comingSoonJournal];

  return (
    <div className="container mt-4">
      <h1>Journal Archive</h1>
      <p className="lead mb-5">
        Browse our complete collection of published volumes and issues.
      </p>

      <div className="row">
        {allJournals.map((journal, index) => {
          // 判断是否为 Protein & Peptide Letters
          const isProteinJournal = journal.journal === "Protein & Peptide Letters";

          return (
            <div key={index} className="col-md-3 mb-3">
              <div
                className="card p-3 h-100 shadow-sm"
                onClick={() => {
                  handleJournalClick(journal.journal);
                }}
              >
                <div
                  className="card-body p-0 d-flex position-relative overflow-hidden flex-column align-items-center justify-content-between text-center"
                  style={{ height: "100%" }}
                >
                  {!journal.isComingSoon ? (
                    <>
                      <img
                        src={journal.cover_image}
                        alt={journal.journal}
                        style={{
                          width: "100%",
                          maxHeight: "400px",
                          flex: "1 1 auto",
                        }}
                      />
                      <h5 className="m-0 pt-4 archive-title">{journal.journal}</h5>
                    </>
                  ) : (
                    <>
                      <div
                        style={{
                          flex: "1 1 auto",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: "100px",
                          height: "395px",
                          lineHeight: "1",
                        }}
                      >
                        {journal.more}
                      </div>
                      <h5 className="m-0 pt-4 archive-title">{journal.journal}</h5>
                    </>
                  )}
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  );
};

export default ArchivePage;